import React, { useCallback, useContext, useMemo } from 'react';
import { Button, Form, FormInstance, message, Select } from 'antd';
import SessionContext from '@providers/Session/SessionContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import api from '@services/api';
import { IDevice } from '@pages/Assets/Inventory/types';
import CustomTag from '@components/CustomTag';

interface TransferPayloadProps {
  action: ActionTypes.TRANSFER;
  devices: {
    msisdn: string;
    iccid: string;
    newCustomerVirtualAccountId: number;
  }[];
}

interface TransferProps {
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useTransfer = ({
  devices,
  isUpload,
  form,
}: TransferProps): DeviceActionProps => {
  const { t } = useTranslation('actions-modal');
  const {
    selectedApp,
    theme,
    isMotEnterprise,
    customer: { customersSelectOptions, customerLogged },
  } = useContext(SessionContext);
  const navigate = useNavigate();
  const customerId = Form.useWatch('customerId', form);
  const contractId = Form.useWatch('contractId', form);
  const requestId = Form.useWatch('requestId', form);

  const customerToGetContracts = useMemo(
    () => (customerId === 0 ? customerLogged?.id : customerId),
    [customerLogged?.id, customerId],
  );

  const { data: contractsByClient } = useSwr(
    isMotEnterprise && customerToGetContracts
      ? `/service-proxy/broker/${customerToGetContracts}/virtual-plan`
      : null,
  );

  const { data: requestsByContract } = useSwr(
    isMotEnterprise && contractId
      ? `/service-proxy/broker/virtual-account?contractId=${contractId}&linesPerPage=${99999}`
      : null,
  );

  const { data: groupingsByClient } = useSwr(
    customerId
      ? `/service-proxy/broker/customers/${customerId}/inventory-groups`
      : null,
  );

  const contractsOptions = useMemo(() => {
    if (contractsByClient === '') {
      return [];
    }
    return contractsByClient
      ? contractsByClient?.map(({ id, planDesc }: any) => ({
          label: planDesc,
          value: id,
        }))
      : undefined;
  }, [contractsByClient]);

  const requestsOptions = useMemo(
    () =>
      requestsByContract
        ? requestsByContract?.content?.map(({ id, name }: any) => ({
            label: name,
            value: id,
          }))
        : undefined,
    [requestsByContract],
  );

  const groupingsOptions = useMemo(
    () =>
      groupingsByClient?.map(({ id, name }: any) => ({
        label: name,
        value: id,
      })),
    [groupingsByClient],
  );

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const typeOfCustomer = t(
      `transfer.${isMotEnterprise ? 'customer' : 'department'}`,
    );
    const typeOfContract = t(
      `transfer.${isMotEnterprise ? 'contract' : 'grouping'}`,
    )?.toLowerCase();
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: `${t('transfer.destiny')} ${
          customerId > 0 ? `(${typeOfCustomer})` : ''
        }`,
        name: 'customerId',
        showItemMessage:
          customerId !== 0 &&
          (isMotEnterprise
            ? contractsOptions?.length === 0
            : groupingsOptions?.length === 0),
        itemMessage: t('none-found', {
          type: typeOfContract,
        }),
        content: (
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            options={[
              {
                label: t('transfer.my-stock'),
                value: 0,
              },
              {
                label: typeOfCustomer,
                options: customersSelectOptions ?? [],
              },
            ]}
            filterOption={(input, option) => {
              const verifyString = (str: string) =>
                String(str).toLowerCase().indexOf(input.toLowerCase()) >= 0;
              // @ts-ignore
              return verifyString(option?.filterProps);
            }}
          />
        ),
      },
    ];
    if (customerId && customerId !== 0) {
      if (isMotEnterprise) {
        defaultSteps.push(
          {
            title: t('transfer.contract'),
            name: 'contractId',
            showItemMessage: requestsOptions?.length === 0,
            itemMessage: t('none-found', {
              type: t('transfer.request')?.toLowerCase(),
            }),
            content: (
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                options={contractsOptions}
                loading={!contractsOptions}
                disabled={!contractsOptions || contractsOptions?.length === 0}
              />
            ),
          },
          {
            title: t('transfer.request'),
            name: 'requestId',
            content: (
              <Select
                showSearch
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={requestsOptions}
                loading={!requestsOptions}
                disabled={!requestsOptions || requestsOptions?.length === 0}
              />
            ),
          },
        );
      } else if (!isMotEnterprise) {
        defaultSteps.push({
          title: t('transfer.grouping'),
          name: 'requestId',
          content: (
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={groupingsOptions}
              loading={!groupingsOptions}
              disabled={!groupingsOptions || groupingsOptions?.length === 0}
            />
          ),
        });
      }
    }

    return defaultSteps;
  }, [
    contractsOptions,
    customerId,
    customersSelectOptions,
    groupingsOptions,
    isMotEnterprise,
    requestsOptions,
    t,
  ]);

  const payloadToSend = useMemo(
    (): TransferPayloadProps => ({
      action: ActionTypes.TRANSFER,
      // @ts-ignore
      devices: isUpload
        ? devices
        : devices?.map(device => ({
            msisdn: device.msisdn,
            iccid: device.iccid,
            newCustomerVirtualAccountId: customerId === 0 ? 0 : requestId,
          })),
    }),
    [devices, customerId, requestId, isUpload],
  );

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}/mobile-network/operational/transfers`;
    message.success(
      <>
        <span>{t('msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('msgs.follow-in-the-transfers-queue')}
        </Button>
      </>,
      2,
    );
  }, [t, navigate, selectedApp]);

  const sendDeviceAction = useCallback(async () => {
    await api.patch('/service-proxy/sims/transfer', payloadToSend?.devices);
    handleSuccess();
  }, [handleSuccess, payloadToSend]);

  const uploadCustomInfo = useMemo(
    () => (
      <span style={{ color: theme?.colors?.text1 }}>
        {t('msgs.transfer-to-stock')}
        <CustomTag>0</CustomTag>
        {t('msgs.transfer-to-stock-2')}
      </span>
    ),
    [t, theme?.colors?.text1],
  );

  return {
    steps,
    payloadToSend,
    uploadCustomInfo,
    sendDeviceAction,
  };
};

export default useTransfer;
